/**
 * Auditoria 9 - Item 3: Generador de propuestas comerciales
 * Reusable dialog: se abre desde detalle de Lead o de Tenant.
 * Selecciona plan + add-ons -> desglose de precios -> PDF con branding.
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Card } from '../ui/card';
import { Loader2, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function ProposalGeneratorDialog({ open, onOpenChange, target, targetType, apiCall }) {
    // target: {id, company_name|name, email|contact_email, ...}
    const [tenantType, setTenantType] = useState(target?.tenant_type || 'airline');
    const [plans, setPlans] = useState([]);
    const [addons, setAddons] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState('pro');
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [aircraftCount, setAircraftCount] = useState(1);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTenantType(target?.tenant_type || 'airline');
        setSelectedAddons([]);
        setPreview(null);
        setAircraftCount(Number(target?.fleet_size || target?.aircraft_count || 1));
    }, [open, target]);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const res = await axios.get(`${API}/public/modules/catalog?tenant_type=${tenantType}`);
                setPlans(res.data.plans || []);
                setAddons(res.data.addons || []);
            } catch (e) {
                console.error(e);
            }
        })();
    }, [open, tenantType]);

    const toggleAddon = (key) => {
        setSelectedAddons(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    const handlePreview = async () => {
        setLoading(true);
        try {
            const res = await apiCall('post', '/admin/proposals', {
                target_type: targetType,
                target_id: target.id,
                tenant_type: tenantType,
                plan: selectedPlan,
                aircraft_count: Number(aircraftCount) || 0,
                addons: selectedAddons,
                billing_cycle: billingCycle,
                notes,
            });
            setPreview(res.data);
            toast.success('Propuesta generada');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error generando propuesta');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!preview?.id) return;
        setGenerating(true);
        try {
            const token = localStorage.getItem('aeroops_admin_token');
            const res = await fetch(`${API}/admin/proposals/${preview.id}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Error PDF');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `AeroOps-Propuesta-${preview.target_name || 'cliente'}-${preview.created_at.slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('PDF descargado');
        } catch (e) {
            toast.error('No se pudo descargar el PDF');
        } finally {
            setGenerating(false);
        }
    };

    const targetName = target?.company_name || target?.name || '(sin nombre)';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[750px] max-h-[92vh] overflow-y-auto" data-testid="proposal-generator-dialog">
                <DialogHeader>
                    <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight flex items-center gap-2">
                        <FileText className="w-5 h-5" /> Generador de Propuesta
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">Para {targetName}</p>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Tipo de tenant</Label>
                            <Select value={tenantType} onValueChange={setTenantType}>
                                <SelectTrigger data-testid="proposal-tenant-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="airline">Aerolinea</SelectItem>
                                    <SelectItem value="ground_handler">Ground Handling</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Plan base</Label>
                            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                                <SelectTrigger data-testid="proposal-plan"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {plans.map(p => (
                                        <SelectItem key={p.key} value={p.key}>
                                            {(p.name || p.key)} — ${p.price_per_aircraft}/aeronave ({p.min_aircraft}{p.max_aircraft ? `–${p.max_aircraft}` : '+'})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Aeronaves facturables</Label>
                            <Input type="number" min="0" value={aircraftCount} onChange={(e) => setAircraftCount(e.target.value)} data-testid="proposal-aircraft-count" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Ciclo</Label>
                            <Select value={billingCycle} onValueChange={setBillingCycle}>
                                <SelectTrigger data-testid="proposal-cycle"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="monthly">Mensual</SelectItem>
                                    <SelectItem value="yearly">Anual</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs mb-2 block">Modulos Add-on</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-1">
                            {addons.map(a => (
                                <label key={a.key} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${a.coming_soon ? 'opacity-60 cursor-not-allowed border-border' : selectedAddons.includes(a.key) ? 'cursor-pointer border-primary bg-primary/5' : 'cursor-pointer border-border hover:border-primary/50'}`} data-testid={`proposal-addon-${a.key}`}>
                                    <Checkbox checked={selectedAddons.includes(a.key)} disabled={a.coming_soon} onCheckedChange={() => toggleAddon(a.key)} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-bold text-sm">{a.label}</span>
                                            {a.coming_soon ? <span className="text-[10px] font-bold uppercase text-amber-500">Próximamente</span> : <span className="font-mono text-sm text-primary">${a.price_monthly_usd}/m</span>}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label className="text-xs">Notas (opcional)</Label>
                        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Contexto adicional para el cliente, promociones, descuentos, etc." data-testid="proposal-notes" />
                    </div>

                    {preview && (
                        <Card className="p-4 bg-muted/30" data-testid="proposal-preview">
                            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Resumen de Inversion</div>
                            <div className="space-y-1.5 text-sm">
                                <div className="flex justify-between"><span>{preview.plan.aircraft_count} aeronaves × {fmt(preview.plan.price_per_aircraft_usd)}</span><span className="font-mono">{fmt(preview.totals.plan_monthly_usd)}/mes</span></div>
                                <div className="flex justify-between"><span>Add-ons ({preview.addons.length})</span><span className="font-mono">{fmt(preview.totals.addons_monthly_usd)}/mes</span></div>
                                <div className="flex justify-between border-t pt-1.5 mt-1.5 font-bold"><span>Total mensual</span><span className="font-mono text-primary">{fmt(preview.totals.total_monthly_usd)}/mes</span></div>
                                <div className="flex justify-between text-muted-foreground text-xs"><span>Total anual (mensual x12)</span><span className="font-mono">{fmt(preview.totals.total_monthly_usd * 12)}</span></div>
                                <div className="flex justify-between font-bold"><span>Total anual (fact. anual)</span><span className="font-mono text-primary">{fmt(preview.totals.total_yearly_usd)}</span></div>
                                {preview.totals.yearly_savings_usd > 0 && (
                                    <div className="flex justify-between text-emerald-600 text-xs font-bold"><span>Ahorro pagando anual</span><span className="font-mono">{fmt(preview.totals.yearly_savings_usd)}</span></div>
                                )}
                            </div>
                        </Card>
                    )}
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
                    <Button onClick={handlePreview} disabled={loading} data-testid="proposal-generate-btn">
                        {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        {preview ? 'Regenerar' : 'Generar propuesta'}
                    </Button>
                    {preview && (
                        <Button className="btn-primary" onClick={handleDownloadPdf} disabled={generating} data-testid="proposal-download-pdf">
                            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                            Descargar PDF
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ProposalGeneratorDialog;
